import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import {
  Star,
  ChevronsUpDown,
  Check,
  Search,
  Loader2,
  X,
  Bot,
  Plus
} from 'lucide-react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import UserContext from '../../../context/UserContext/UserContext';
import ChatbotFavouriteService from '../../../services/ChatbotFavouriteService/ChatbotFavouriteService';
import Chatbot from '../../../types/Chatbot';
import Button from '../../Button/Button';
import CustomTooltip from '../../CustomTooltip/CustomTooltip';

interface ChatbotSelectorProps {
  chatbots: Chatbot[];
  loading: boolean;
  onCreate?: () => void;
  collapsed?: boolean;
}

const MAX_VISIBLE_CHARS = 22;

function ChatbotSelector({
  chatbots,
  loading,
  onCreate,
  collapsed = false
}: Readonly<ChatbotSelectorProps>): React.ReactElement {
  const { currentChatbot, setCurrentChatbot } = useContext(ChatbotContext);
  const { user } = useContext(UserContext);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [favourites, setFavourites] = useState<Set<number>>(() => {
    const stored = localStorage.getItem('chatbotFavourites');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const favouritesLoadedRef = useRef(false);

  const chatbotName = currentChatbot?.name ?? 'Select a chatbot';
  const shouldShowTooltip = chatbotName.length > MAX_VISIBLE_CHARS;

  useEffect(() => {
    localStorage.setItem(
      'chatbotFavourites',
      JSON.stringify(Array.from(favourites))
    );
  }, [favourites]);

  useEffect(() => {
    if (!user || favouritesLoadedRef.current) return;

    favouritesLoadedRef.current = true;
    ChatbotFavouriteService.getFavourites()
      .then((ids) => {
        const newFavs = new Set(ids);
        if (
          JSON.stringify(Array.from(newFavs)) !==
          JSON.stringify(Array.from(favourites))
        ) {
          setFavourites(newFavs);
        }
      })
      .catch(() => {
        favouritesLoadedRef.current = false;
        setError('Error loading favourites');
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      });
  }, [user?.id, favourites]);

  const handleTogglePopover = (): void => {
    if (!isOpen) {
      setSearch('');
    }
    setIsOpen(!isOpen);
  };

  const filteredChatbots = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...chatbots]
      .filter((c) => c.name.toLowerCase().includes(query))
      .sort((a, b) => {
        if (currentChatbot?.id === a.id) return -1;
        if (currentChatbot?.id === b.id) return 1;
        const aFav = favourites.has(a.id) ? -1 : 1;
        const bFav = favourites.has(b.id) ? -1 : 1;
        return aFav - bFav;
      });
  }, [chatbots, search, favourites, currentChatbot]);

  const handleSelect = (chatbotId: number): void => {
    setCurrentChatbot(chatbotId);
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        isOpen &&
        popoverRef.current &&
        triggerRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return (): void =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleFavourite = (chatbotId: number): void => {
    if (favourites.has(chatbotId)) {
      ChatbotFavouriteService.removeFavourite(chatbotId)
        .then(() => {
          setFavourites((prev) => {
            const next = new Set(prev);
            next.delete(chatbotId);
            return next;
          });
        })
        .catch(() => {
          setError('Failed to remove favourite');
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
        });
    } else {
      ChatbotFavouriteService.addFavourite(chatbotId)
        .then(() => {
          setFavourites((prev) => {
            const next = new Set(prev);
            next.add(chatbotId);
            return next;
          });
        })
        .catch(() => {
          setError('Failed to add favourite');
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
        });
    }
  };

  return (
    <div className={collapsed ? 'relative' : 'relative w-full px-0 py-0.5'}>
      <div ref={triggerRef}>
        {collapsed ? (
          <CustomTooltip content={chatbotName} className='text-white'>
            <Button
              variant='outline'
              onClick={handleTogglePopover}
              aria-label='Switch chatbot'
              className={`relative h-10 w-10 p-0 flex items-center justify-center
                bg-[#057D99] text-white border-white/30 hover:bg-[#012438]
                hover:text-white rounded-md transition-colors`}
            >
              <Bot className='h-5 w-5' />
              {favourites.has(currentChatbot?.id ?? -1) && (
                <Star className='absolute -top-1 -right-1 h-3 w-3 fill-yellow-400 text-yellow-400' />
              )}
            </Button>
          </CustomTooltip>
        ) : (
          <Button
            variant='outline'
            onClick={handleTogglePopover}
            className={`w-full justify-between bg-[#057D99] text-white border-white/30 hover:bg-[#012438] hover:text-white
             h-10 pl-4 pr-2 py-2 rounded-md transition-colors text-base`}
          >
            <div className='flex items-center min-w-0 flex-1'>
              <Bot className='mr-2 h-4 w-4 flex-shrink-0 opacity-80' />
              {shouldShowTooltip ? (
                <CustomTooltip content={chatbotName} className='text-white'>
                  <span className='truncate'>{chatbotName}</span>
                </CustomTooltip>
              ) : (
                <span className='truncate'>{chatbotName}</span>
              )}
            </div>
            {favourites.has(currentChatbot?.id ?? -1) && (
              <Star className='ml-2 h-4 w-4 fill-yellow-400 text-yellow-400 flex-shrink-0' />
            )}
            <ChevronsUpDown className='ml-4 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        )}
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className={`absolute ${collapsed ? 'left-full top-0 ml-2' : 'top-full left-0 mt-1'}
            bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden`}
          style={{ width: 'min(384px, calc(100vw - 6rem))' }}
        >
          <div className='flex items-center border-b px-4 py-3'>
            <Search size={16} className='text-gray-400 mr-2 flex-shrink-0' />
            <input
              ref={searchInputRef}
              type='text'
              placeholder='Search chatbot...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='flex-1 text-base bg-transparent outline-none text-gray-900 placeholder:text-gray-400'
            />
            {search && (
              <button
                type='button'
                onClick={() => {
                  setSearch('');
                  searchInputRef.current?.focus();
                }}
                className='ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0'
                aria-label='Clear search'
              >
                <X size={16} />
              </button>
            )}
          </div>

          {loading && (
            <div className='flex justify-center items-center py-4'>
              <Loader2 size={20} className='animate-spin text-gray-400' />
            </div>
          )}
          {!loading && filteredChatbots.length === 0 && (
            <div className='flex justify-center items-center py-4 text-gray-500 text-sm'>
              No chatbots found
            </div>
          )}
          {!loading && filteredChatbots.length > 0 && (
            <div className='max-h-60 overflow-y-auto'>
              {filteredChatbots.map((chatbot) => (
                <Button
                  key={chatbot.id}
                  variant='ghost'
                  className='flex items-center justify-between px-3 py-2 hover:bg-gray-50 w-full text-left h-auto text-base font-normal'
                  onClick={() => handleSelect(chatbot.id)}
                >
                  <div className='flex items-center flex-1 min-w-0'>
                    <div className='w-6 flex justify-center mr-2'>
                      {currentChatbot?.id === chatbot.id && (
                        <Check size={16} className='text-green-600' />
                      )}
                    </div>
                    <div className='text-base text-gray-900 truncate flex-1 min-w-0'>
                      {chatbot.name}
                    </div>
                  </div>
                  <Star
                    size={16}
                    className={`cursor-pointer ml-2 flex-shrink-0 ${
                      favourites.has(chatbot.id)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavourite(chatbot.id);
                    }}
                  />
                </Button>
              ))}
            </div>
          )}

          {onCreate && (
            <button
              type='button'
              onClick={() => {
                setIsOpen(false);
                onCreate();
              }}
              className='flex items-center gap-2 w-full px-4 py-3 border-t text-base text-[#057D99] hover:bg-gray-50'
            >
              <Plus size={16} className='flex-shrink-0' />
              Create chatbot
            </button>
          )}
        </div>
      )}

      {showError && (
        <div className='fixed bottom-4 left-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50'>
          <div className='flex items-center gap-2'>
            <span className='text-sm'>{error}</span>
            <button
              type='button'
              onClick={() => setShowError(false)}
              className='text-white/70 hover:text-white ml-2'
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatbotSelector;
